import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-language";
import { supabase } from "@/integrations/supabase/client";
import { JButton } from "@/components/JButton";
import { toast } from "sonner";
import { Globe, Clock, LogOut, Shield, Bell, Trash2, FileText, HelpCircle, CreditCard } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { deleteOwnAccount } from "@/lib/account.functions";
import { useServerFn } from "@tanstack/react-start";
import { ParentInbox } from "@/components/ParentInbox";


export const Route = createFileRoute("/_authenticated/app/settings")({
  component: Settings,
});

function Settings() {
  const { user, signOut } = useAuth();
  const { lang, setLang, t } = useLang();
  const [displayName, setDisplayName] = useState("");
  const [limit, setLimit] = useState<number>(() =>
    typeof window === "undefined" ? 30 : Number(localStorage.getItem("jozori_daily_limit") ?? 30),
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const callDelete = useServerFn(deleteOwnAccount);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("display_name")
      .eq("id", user.id)
      .single()
      .then(({ data }) => data?.display_name && setDisplayName(data.display_name));
  }, [user]);

  const saveProfile = async () => {
    if (!user) return;
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: displayName.trim() || "Parent" })
      .eq("id", user.id);
    if (error) return toast.error(error.message);
    toast.success("Saved");
  };

  const saveLimit = (n: number) => {
    setLimit(n);
    localStorage.setItem("jozori_daily_limit", String(n));
  };

  return (
    <div className="px-5 py-8 max-w-2xl mx-auto">
      <div className="mb-4"><BackButton /></div>
      <p className="font-display-en text-xs uppercase tracking-[0.3em] text-primary font-bold mb-2">
        {t("settings")}
      </p>
      <h1 className="font-display-en text-3xl font-bold tracking-tight mb-6">
        Family preferences
      </h1>

      <Section icon={<Globe className="size-4" />} title={t("language")}>
        <div className="grid grid-cols-2 gap-3">
          <LangBtn active={lang === "ar"} onClick={() => setLang("ar")}>
            <span className="font-display-ar text-xl">العربية</span>
          </LangBtn>
          <LangBtn active={lang === "en"} onClick={() => setLang("en")}>
            <span className="font-display-en text-lg font-bold">English</span>
          </LangBtn>
        </div>
      </Section>

      <Section icon={<Clock className="size-4" />} title={t("screenTime")}>
        <p className="text-sm text-foreground/70 mb-3">
          {limit} {t("minutes")} per day per child
        </p>
        <input
          type="range"
          min={10}
          max={120}
          step={5}
          value={limit}
          onChange={(e) => saveLimit(Number(e.target.value))}
          className="w-full accent-primary"
        />
        <div className="flex justify-between text-xs text-foreground/50 mt-1">
          <span>10m</span>
          <span>120m</span>
        </div>
      </Section>

      <Section icon={<Shield className="size-4" />} title="Parent name">
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={40}
          className="w-full px-4 py-3 rounded-2xl border-2 border-clay/40 bg-card focus:border-primary focus:outline-none"
        />
        <JButton size="md" className="mt-3" onClick={saveProfile}>
          Save
        </JButton>
      </Section>

      <Section icon={<Bell className="size-4" />} title="Notifications">
        <p className="text-sm text-foreground/60">
          Weekly summaries arrive in your inbox every Sunday.
        </p>
      </Section>

      <ParentInbox isAr={lang === "ar"} />

      <Section icon={<FileText className="size-4" />} title={lang === "ar" ? "قانوني ودعم" : "Legal & support"}>
        <div className="grid gap-2">
          <LegalLink to="/support" icon={<HelpCircle className="size-4" />}>
            {lang === "ar" ? "مركز الدعم" : "Support center"}
          </LegalLink>
          <LegalLink to="/contact" icon={<HelpCircle className="size-4" />}>
            {lang === "ar" ? "تواصل معنا" : "Contact us"}
          </LegalLink>
          <LegalLink to="/privacy" icon={<Shield className="size-4" />}>
            {lang === "ar" ? "سياسة الخصوصية" : "Privacy policy"}
          </LegalLink>
          <LegalLink to="/terms" icon={<FileText className="size-4" />}>
            {lang === "ar" ? "شروط الاستخدام" : "Terms of use"}
          </LegalLink>
          <LegalLink to="/refunds" icon={<CreditCard className="size-4" />}>
            {lang === "ar" ? "سياسة الاسترداد" : "Refund policy"}
          </LegalLink>
        </div>
      </Section>

      <JButton
        variant="outline"
        size="lg"
        className="w-full mt-4"
        onClick={async () => {
          await signOut();
          window.location.href = "/";
        }}
      >
        <LogOut className="size-4" /> {t("signOut")}
      </JButton>

      <Section icon={<Trash2 className="size-4 text-destructive" />} title="Danger zone">
        <p className="text-sm text-foreground/70 mb-3">
          {lang === "ar"
            ? "حذف حسابك سيمسح جميع بيانات العائلة والأطفال والاشتراكات نهائيًا. لا يمكن التراجع."
            : "Deleting your account permanently erases all family, child, and subscription data. This cannot be undone."}
        </p>
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            className="w-full py-3 rounded-2xl border-2 border-destructive/40 text-destructive font-semibold hover:bg-destructive/10 transition-colors"
          >
            {lang === "ar" ? "حذف حسابي" : "Delete my account"}
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-foreground/60">
              {lang === "ar" ? 'اكتب "DELETE" للتأكيد:' : 'Type "DELETE" to confirm:'}
            </p>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border-2 border-destructive/40 bg-background focus:border-destructive focus:outline-none"
              placeholder="DELETE"
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setConfirmDelete(false);
                  setConfirmText("");
                }}
                className="flex-1 py-3 rounded-2xl border-2 border-clay/40 font-semibold"
                disabled={deleting}
              >
                {lang === "ar" ? "إلغاء" : "Cancel"}
              </button>
              <button
                disabled={confirmText !== "DELETE" || deleting}
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await callDelete();
                    await supabase.auth.signOut();
                    toast.success(lang === "ar" ? "تم حذف حسابك" : "Account deleted");
                    window.location.href = "/";
                  } catch (e: any) {
                    toast.error(e?.message || "Failed to delete account");
                    setDeleting(false);
                  }
                }}
                className="flex-1 py-3 rounded-2xl bg-destructive text-destructive-foreground font-semibold disabled:opacity-50"
              >
                {deleting
                  ? lang === "ar"
                    ? "جارٍ الحذف…"
                    : "Deleting…"
                  : lang === "ar"
                    ? "حذف نهائي"
                    : "Permanently delete"}
              </button>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card border-2 border-clay/30 rounded-3xl p-5 mb-4">
      <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest font-bold text-foreground/60">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function LangBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`py-4 rounded-2xl border-2 transition-all ${
        active ? "border-primary bg-primary/10 scale-[1.02]" : "border-clay/40 bg-card"
      }`}
    >
      {children}
    </button>
  );
}

function LegalLink({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-4 py-3 rounded-2xl border-2 border-clay/30 bg-background hover:border-primary/40 transition-colors text-sm font-semibold"
    >
      <span className="text-primary">{icon}</span>
      <span className="flex-1">{children}</span>
      <span className="text-foreground/40">→</span>
    </Link>
  );
}

